// ============================================================================
// ConfirmDialog — a small, centered, accessible confirmation modal used for the
// destructive delete-program action. Closes on Escape / backdrop click; locks
// body scroll; restores focus on close; reduced-motion aware. Page-local so the
// Programs screen never edits shared components.
// ============================================================================
import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Banner, Button } from "../../components";

interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Show an error banner inside the dialog (e.g. delete failed). */
  error?: ReactNode;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  error,
  busy = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const prevActive = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const id = window.setTimeout(() => {
      panelRef.current
        ?.querySelector<HTMLElement>("button:not([disabled])")
        ?.focus();
    }, 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(id);
      prevActive?.focus?.();
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            ref={panelRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.24, ease: [0.34, 1.56, 0.64, 1] }}
            className="relative w-full max-w-sm rounded-xl bg-paper p-6 shadow-lg"
          >
            <h2
              id={titleId}
              className="font-display text-xl font-extrabold tracking-tightest text-body"
            >
              {title}
            </h2>
            <p className="mt-2 text-sm text-body-soft">{body}</p>

            {error && (
              <div className="mt-4">
                <Banner tone="danger">{error}</Banner>
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                {cancelLabel}
              </Button>
              <Button variant="destructive" onClick={onConfirm} disabled={busy}>
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
